import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useCancelableEffect} from '../../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {boxedInputColorReaders} from '../../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputConstants';
import {DropdownBoxCoordinator} from '../../../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {Button} from '../../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../../../../../libs/shared/ui/src/styles/commonStyles';
import {ellipsis} from '../../../../../../../libs/shared/ui/src/styles/mixins';
import {useAppDispatch, useAppSelector} from '../../../../../../helpers/appReduxHelpers';
import {AccountModel} from '../../../../../../models/api/accountModel';
import {fetchAccountByAliasAsyncAction} from '../../../../../../store/data/accounts/accountsActions';
import {getAccounts} from '../../../../../../store/data/dataSelectors';
import {AccountAvatar} from '../../../../../common/avatars/accountAvatar';
import {SearchAccountsDropdown} from '../../../../../common/dropdowns/search/searchAccountsDropdown';

interface SingleSelectAccountFieldProps {
  selectedAccountAlias: string | undefined;
  onAccountAliasSelection: (arg: string | undefined) => void;
}

const StyledPlaceholderSpan = styled.span`
  color: ${boxedInputColorReaders.textPlaceholder};
`;

const StyledButtonContentDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StyledEllidedSpan = styled.span`
  ${ellipsis()}
`;

export const SingleSelectAccountField: FC<SingleSelectAccountFieldProps> = (props) => {
  const {selectedAccountAlias, onAccountAliasSelection} = props;
  const dispatch = useAppDispatch();
  const accounts = useAppSelector(getAccounts);
  const [selectedAccount, setSelectedAccount] = useState<AccountModel | undefined>(undefined);

  useCancelableEffect(
    (cancelToken) => {
      if (selectedAccountAlias) {
        // Try to find the account in the store first.
        const storeAccount = Object.values(accounts).find(
          (account) => account?.alias === selectedAccountAlias,
        );

        if (storeAccount) {
          setSelectedAccount(storeAccount);
        } else {
          // Fetch it otherwise.
          dispatch(fetchAccountByAliasAsyncAction(selectedAccountAlias, {cancelToken})).then(
            setSelectedAccount,
          );
        }
      } else {
        setSelectedAccount(undefined);
      }
    },
    [selectedAccountAlias],
  );

  return (
    <DropdownBoxCoordinator
      leftIcon={!selectedAccountAlias ? 'briefcaseOutlineSmall' : undefined}
      value={renderInputTitle(selectedAccountAlias, selectedAccount)}
      renderDropdown={(popoverProps) => (
        <SearchAccountsDropdown
          popoverProps={popoverProps}
          selectedAccountId={selectedAccount?.id}
          onConfirm={(account) => {
            onAccountAliasSelection(account.alias);
          }}
          headerRightIcon={
            selectedAccountAlias ? (
              <Button
                variant={ButtonVariantsEnum.LINK}
                size={VisualSizesEnum.SMALL}
                onClick={() => {
                  onAccountAliasSelection(undefined);
                  popoverProps.onRequestClose();
                }}
              >
                <Trans id="clear-refinement-button-link">Clear</Trans>
              </Button>
            ) : null
          }
        />
      )}
      shouldDisablePadding
    />
  );
};

function renderInputTitle(
  selectedAccountAlias: string | undefined,
  selectedAccount: AccountModel | undefined,
) {
  if (!selectedAccountAlias) {
    return (
      <StyledPlaceholderSpan>
        <Trans id="search-query-builder-account-placeholder">Select account</Trans>
      </StyledPlaceholderSpan>
    );
  }

  return (
    <StyledButtonContentDiv>
      <AccountAvatar account={selectedAccount} avatarIconSize={12} size={VisualSizesEnum.MEDIUM} />
      <StyledEllidedSpan>{selectedAccount?.name ?? selectedAccountAlias}</StyledEllidedSpan>
    </StyledButtonContentDiv>
  );
}
